/**
 * Background check status, mirroring the .NET BackgroundCheckStatus enum.
 *
 * The BE uses a two-enum model (status + outcome) inherited from Sterling — there
 * is no flat "Cleared" or "Flagged" value. When status === Completed, inspect the
 * outcome to know whether the report came back Clear, Consider, or Suspended.
 *
 * A null/undefined status (no record on the BE) means the contractor has not
 * started the flow yet — there is no NotStarted enum value.
 */
export enum BackgroundCheckStatusEnum {
  Initiated = 'Initiated',
  InProgress = 'InProgress',
  Completed = 'Completed',
  Expired = 'Expired',
  Cancelled = 'Cancelled',
  Error = 'Error',
  PendingContractorConsent = 'PendingContractorConsent'
}

/** Outcome only meaningful when status === Completed. */
export enum BackgroundCheckOutcomeEnum {
  Clear = 'Clear',
  Consider = 'Consider',
  Suspended = 'Suspended'
}

export enum BackgroundCheckPaymentTypeEnum {
  Upfront = 'Upfront',
  Deferred = 'Deferred',
  NotApplicable = 'NotApplicable'
}

export enum BackgroundCheckPaymentStatusEnum {
  NotApplicable = 'NotApplicable',
  Pending = 'Pending',
  Paid = 'Paid',
  Deducted = 'Deducted',
  Failed = 'Failed'
}

export interface BackgroundCheckResponse {
  id: string;
  status: BackgroundCheckStatusEnum;
  /** Human-readable status label from the backend (e.g. 'Invitation Sent'). Fallback for unknown enum values. */
  statusDisplay?: string;
  outcome?: BackgroundCheckOutcomeEnum | null;
  /** Human-readable outcome label from the backend. Fallback for unknown enum values. */
  outcomeDisplay?: string | null;
  /** FCRA adjudication stage set by Checkr. Takes precedence over outcome for display. */
  adjudicationStatus?: 'Engaged' | 'PreAdverseAction' | 'PostAdverseAction' | 'Disputed' | null;
  /** True when a completed Checkr report had one or more screenings canceled ("Clear with Canceled" case). */
  includesCanceledScreenings?: boolean;
  /** Checkr candidate dashboard URL; present once the report is completed. */
  reportUrl?: string | null;
  /** True once a Checkr report PDF is on file and available via the proxy endpoint. */
  reportPdfAvailable?: boolean;
  /** Whether the contractor has consented to facilities viewing their background check report. */
  facilityReportConsentGiven?: boolean;
  /** Timestamp when the contractor granted facility report consent. Audit only — no UI needed. */
  facilityReportConsentAt?: string | null;
  provider?: string;
  paymentType: BackgroundCheckPaymentTypeEnum;
  paymentStatus: BackgroundCheckPaymentStatusEnum;
  invitationUrl?: string;
  completedAt?: string;
  createdAt: string;
}

export interface InitiateBackgroundCheckRequest {
  paymentType: BackgroundCheckPaymentTypeEnum;
  /** Required when paymentType === Upfront, must be omitted for Deferred. */
  stripePaymentIntentId?: string;
  /** Contractor consent for facilities to view their background check report. Required — BE returns 400 if false. */
  facilityReportConsent: boolean;
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amountInCents: number;
  currency: string;
}

/**
 * Request body for POST /api/contractors/me/background-check/upload.
 * File content is base64-encoded — matches the JSON-body convention used
 * for all contractor document uploads on this platform.
 */
export interface BackgroundCheckUploadRequest {
  fileName: string;
  fileContent: string;
  mimeType: string;
  expirationDate: string | null;
}

/** Returned by GET /api/contractors/me/profile-completion. */
export interface ProfileCompletionResponse {
  isComplete: boolean;
  steps: {
    bankAccountConfigured: boolean;
    stripeOnboardingCompleted: boolean;
    taxInfoOnFile: boolean;
  };
}
